import { type HTMLAttributes } from "react";
import { cn } from "@ridereserv/shared/utils";

export type BadgeVariant =
  | "default"
  | "secondary"
  | "success"
  | "warning"
  | "destructive"
  | "live"
  | "outline";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /**
   * Render a leading status dot that inherits the badge text color. Useful for
   * operational status pills (Confirmed / En route / Cancelled). Decorative —
   * the badge label carries the meaning.
   */
  dot?: boolean;
}

/**
 * Badge component for status indicators and labels.
 * Uses semantic color tokens from the design system.
 * @param root0 - Badge props
 * @param root0.className - Additional CSS classes
 * @param root0.variant - Badge variant style
 * @param root0.dot - Render a leading status dot
 * @param root0.children - Badge label
 * @returns A styled badge element
 */
export function Badge({
  className,
  variant = "default",
  dot = false,
  children,
  ...props
}: BadgeProps) {
  const variants: Record<BadgeVariant, string> = {
    default: "bg-primary-100 text-primary-800",
    secondary: "bg-neutral-100 text-neutral-800",
    success: "bg-success-100 text-success-700",
    warning: "bg-warning-100 text-warning-700",
    destructive: "bg-destructive-100 text-destructive-700",
    live: "bg-live-100 text-live-700",
    outline: "border border-neutral-300 bg-transparent text-neutral-700",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        // Only introduce a flex gap when the leading dot renders, so existing
        // badges that space an icon child via its own margin are unaffected.
        dot && "gap-1.5",
        variants[variant],
        className
      )}
      {...props}
    >
      {dot && (
        <span
          className="size-1.5 rounded-full bg-current opacity-80"
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
